import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FileRejection, FileWithPath, useDropzone } from 'react-dropzone'
import { DropzoneVariant } from '~/components/shared/componentStyles/Dropzone.style'

import { Context, contextDefault } from './Context'
import type { FileWithHashKey, Props } from './types'
import { isDiff } from './utils'
import { generateFileHash } from './utils'

export function Provider({
  children,
  props,
  ref,
}: React.PropsWithChildren<{
  props: Props
  ref: React.ForwardedRef<any>
}>) {
  const {
    acceptedFileTypes,
    allowMultiple,
    dropzoneProps,
    maxFileSize,
    onClear,
    onFileChange,
    coverPage,
    handleCoverPageDelete,
  } = props

  const [currentVariant, setCurrentVariant] = useState(
    contextDefault.currentVariant,
  )

  const isDragActivePrev = useRef(false)
  const areRejectionsAccepted = useRef(false)
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([])
  const [acceptedFiles, setAcceptedFiles] = useState<FileWithHashKey[]>(
    contextDefault.acceptedFiles,
  )

  const potentialDuplicatePaths = useMemo(() => {
    const pathSizes: Record<string, number[]> = {}
    const results: string[] = []

    acceptedFiles.forEach((file) => {
      if (file.path) {
        if (!pathSizes[file.path]) {
          pathSizes[file.path] = []
        }

        if (
          pathSizes[file.path].includes(file.size) &&
          !results.includes(file.path)
        ) {
          results.push(file.path)
        }

        pathSizes[file.path].push(file.size)
      }
    })

    return results
  }, [acceptedFiles])

  const totalSize = useMemo(
    () => acceptedFiles.reduce((size, file) => size + file.size, 0),
    [acceptedFiles],
  )

  const dropzoneState = useDropzone({
    accept: acceptedFileTypes,
    maxSize: maxFileSize,
    noClick: currentVariant === DropzoneVariant.FileAccepted,
    ...dropzoneProps,
    maxFiles: allowMultiple ? dropzoneProps.maxFiles : 1,
  })
  const {
    isDragActive,
    isDragAccept,
    isFileDialogActive,
    acceptedFiles: dzAcceptedFiles,
    fileRejections: dzFileRejections,
  } = dropzoneState

  const resetFileState = () => {
    areRejectionsAccepted.current = false
    changeAcceptedFiles([])
    setFileRejections(() => [])
  }

  const resetAllState = () => {
    resetFileState()
    setCurrentVariant(DropzoneVariant.Default)
    onClear?.()
  }

  const updateFiles = (files: {
    accepted?: FileWithPath[]
    rejected?: FileRejection[]
  }) => {
    if (files.accepted && isDiff(files.accepted, acceptedFiles)) {
      for (const file of files.accepted) {
        // Generate a random hash for each file for identification
        // @ts-expect-error we know this is a FileWithPath
        file.hashKey = generateFileHash()
      }
      appendAcceptedFiles(files.accepted as FileWithHashKey[])
    }

    if (files.rejected && isDiff(files.rejected, fileRejections)) {
      appendRejectedFiles(files.rejected)
    }
  }

  const setFiles = useCallback(
    (files: { accepted?: FileWithHashKey[]; rejected?: FileRejection[] }) => {
      if (files.accepted && isDiff(files.accepted, acceptedFiles)) {
        changeAcceptedFiles(files.accepted)
      }

      if (files.rejected && isDiff(files.rejected, fileRejections)) {
        setFileRejections(files.rejected)
      }
    },
    [acceptedFiles, fileRejections],
  )

  function appendAcceptedFiles(next: FileWithHashKey[]) {
    const allFiles = [...acceptedFiles, ...next]
    setAcceptedFiles((state) => [...state, ...next])
    onFileChange(allFiles as File[])
  }

  function appendRejectedFiles(next: FileRejection[]) {
    const allFiles = [...fileRejections, ...next]
    setFileRejections(allFiles)
  }

  function changeAcceptedFiles(next: FileWithHashKey[]) {
    setAcceptedFiles(() => next)
    onFileChange(next as File[])
  }

  //when dzAcceptedFiles or dzFileRejections change - update state
  useEffect(() => {
    areRejectionsAccepted.current = false
    updateFiles({
      accepted: [...dzAcceptedFiles],
      rejected: [...dzFileRejections],
    })
  }, [dzAcceptedFiles, dzFileRejections])

  //select variant based on conditions
  useEffect(() => {
    if (isDragActive && !isDragActivePrev.current) {
      //clear out any previous file rejections or accepted files
      if (isDragAccept) {
        setCurrentVariant(DropzoneVariant.DragAccepted)
      } else {
        setCurrentVariant(DropzoneVariant.Error)
      }
      isDragActivePrev.current = true
    } else if (!isDragActive && isDragActivePrev.current) {
      //if drag was active, but did not result in an error or file accepted, set to default - this indicates the user did not "drop" the file
      if (fileRejections.length <= 0 && acceptedFiles.length <= 0) {
        setCurrentVariant(DropzoneVariant.Default)
      }
      isDragActivePrev.current = false
    } else {
      if (isFileDialogActive) {
        setCurrentVariant(DropzoneVariant.Active)
        return
      }

      if (fileRejections.length > 0) {
        const errorCodes = [
          ...new Set(
            fileRejections.flatMap((fileRejection) =>
              fileRejection.errors.map((error) => error.code),
            ),
          ),
        ]

        // Enables the user to select which files to get rid of before submitting to adhere to size limits.
        if (
          !areRejectionsAccepted.current &&
          allowMultiple &&
          errorCodes.length === 1 &&
          errorCodes[0] === 'file-too-large'
        ) {
          const filesNext = [
            ...acceptedFiles,
            ...fileRejections.map((fileRejection) => fileRejection.file),
          ]
          if (isDiff(filesNext, acceptedFiles)) {
            areRejectionsAccepted.current = true
            updateFiles({
              accepted: filesNext,
              rejected: [], // A reference to these is still accessible via dzFileRejections
            })
            setCurrentVariant(DropzoneVariant.FileAccepted)
          }
          return
        }

        // Something changed, but previously accepted rejections are still in the file list. No-op.
        if (areRejectionsAccepted.current) {
          return
        }

        setCurrentVariant(DropzoneVariant.Error)
        return
      }

      if (acceptedFiles.length > 0) {
        setCurrentVariant(DropzoneVariant.FileAccepted)
        return
      }

      setCurrentVariant(DropzoneVariant.Default)
    }
  }, [
    acceptedFiles,
    fileRejections,
    isDragAccept,
    isDragActive,
    isFileDialogActive,
  ])

  return (
    <Context.Provider
      value={{
        acceptedFiles,
        currentVariant,
        dropzoneState,
        fileRejections,
        potentialDuplicatePaths,
        props,
        ref,
        coverPage,
        resetAllState,
        setCurrentVariant,
        totalSize,
        updateFiles,
        setFiles,
        handleCoverPageDelete,
      }}
    >
      {children}
    </Context.Provider>
  )
}
